import {UserDefinedObjectTemplateAdditionalColumnSourceType as UdoColumnSource} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {UserDefinedObjectTemplateAdditionalColumn as UdoColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import type {IconProp} from "@atlaskit/button/dist/types/entry-points/new";
import type {NewIconProps} from "@atlaskit/icon/dist/types/types";
import {token} from "@atlaskit/tokens";
import {
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/emotorad";
import {EmotoradAssignedDealerSelect} from "~/components/eventContainerComponents/emotoradAssignedDealerSelect";
import {resolveEmotoradLifecycleDealerCityStateZone} from "~/utilities/resolveEmotoradLifecycleDealerCityStateZone";
import {AnankeTempCollapsibleSectionWithIcon} from "~/components/anankeTemp/anankeTempCollapsibleSectionWithIcon";
import {AnankeTempFormCollapsibleSection} from "~/components/anankeTemp/anankeTempFormCollapsibleSection";
import {AnankeTempInlineEditableSelect} from "~/components/anankeTemp/anankeTempInlineEditableSelect";
import {AnankeTempVerticalSpacer} from "~/components/anankeTemp/anankeTempVerticalSpacer";
import {getAdditionalColumnOptions, getProductCatalogueTemplateIdFromAdditionalColumnConfiguration} from "~/domainModels/additionalColumns";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";

import {getSystemAdditionalColumnDetails} from "~/domainModels/systemAdditionalColumns";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import type {EventContainerConstraint} from "~/domainModels/workspaceModules/eventContainersModule";
import {areEventContainerGlobalConstraintsSatisfied} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {isLeafUser} from "~/components/entitySelectors/eventContainerSelector";
import {UserStatus} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {userHasAccessToOrganisationalUnit} from "~/utilities/accessControl/userOuAccess";
import {
    findWorkspaceUserByStoredReferenceId,
    getUserDisplayName,
    type PlatformUserDisplayDetails,
} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";

import {useUsers} from "~/contexts/workspaceUsersContext";
import {
    useOrganizationalUnitTemplates,
    useProductCatalogueTemplates,
    useWorkspace,
    useWorkspaceUserTemplates as useUserTemplates,
} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID,
    isAmaronWorkspace,
} from "~/domainModels/workspaceSpecific/amaron";
import {formatAmaronSourcePriorityScoreForDisplay} from "~/utilities/workspaceSpecific/amaronPriority";
import {buildAmaronAssignedAgentSelectOptions} from "~/utilities/workspaceSpecific/amaronAgentAssignment";
import {AnankeSelect} from "@vesta/ananke/components/anankeSelect";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";

import {getProductCatalogueDisplayName} from "~/utilities/productCatalogueDisplayName";
import {getOptionalEventContainerOrganizationalUnitId} from "~/utilities/ottopilotSemanticColumns";

// Type for a column in the eventContainerDetailsPage.columnGrouping
type ColumnConfig = {
    id: Uuid;
    columnId: Uuid | string;
    columnType: "OttopilotEventContainer" | "OttopilotEvent";
    eventTemplateId?: Uuid;
    isAdditionalColumn: boolean;
    isEditable?: boolean;
    isRequired?: boolean;
    label: string;
};

// Type for a column group in the eventContainerDetailsPage.columnGrouping
type ColumnGroup = {
    id: Uuid;
    displayName: string;
    columns: Array<ColumnConfig>;
};

/** Column group with optional icon config for AnankeTempCollapsibleSectionWithIcon */
export type ColumnGroupWithOptionalIcon = {
    id: Uuid;
    displayName: string;
    columns: Array<ColumnConfig>;
    icon?: IconProp;
    iconColor?: NewIconProps["color"];
    iconBackgroundColor?: string;
    initialIsOpen?: boolean;
    className?: string;
};

type EventContainerDetailsColumnsProps = {
    columnGrouping: Array<ColumnGroup>;
    eventContainerTemplate: EventContainerTemplate;
    eventTemplates: Array<OttopilotEventTemplate>;
    eventContainerWithDiff: EventContainerWithEvents;
    eventContainer: EventContainerWithEvents;
    globalConstraints?: Array<EventContainerConstraint>;
    onColumnValueChange: (columnId: string, value: any, eventTemplateId: Uuid | undefined) => void;
};

type EventContainerEditPanelColumnsProps = {
    columnGrouping: Array<ColumnGroupWithOptionalIcon>;
    eventContainerTemplate: EventContainerTemplate;
    eventTemplates: Array<OttopilotEventTemplate>;
    eventContainerWithDiff: EventContainerWithEvents;
    eventContainer: EventContainerWithEvents;
    onColumnValueChange: (columnId: string, value: any, eventTemplateId: Uuid | undefined) => void;
    useInQueueEditLayout?: boolean;
    fieldGridColumnCount?: number;
    useDayPlanEditLayout?: boolean;
    /** Amaron Leads: only columns marked required show an asterisk (In Queue keeps legacy behavior). */
    useAmaronLeadsStatusHeader?: boolean;
    /** Workspace-specific panels can opt into required-only asterisks without changing their header variant. */
    useRequiredAsteriskOnly?: boolean;
    /**
     * Emotorad lifecycle / lead owner: render a dealer-scoped user select (leaf users with OU access)
     * instead of the default eventContainer user column control.
     */
    emotoradLifecycleOwnerColumnId?: Uuid;
    globalConstraints?: Array<EventContainerConstraint>;
};

function getEventContainerDetailsColumnIsEditable(
    columnConfig: ColumnConfig,
    eventContainer: EventContainerWithEvents,
    globalConstraints?: Array<EventContainerConstraint>,
): boolean {
    if (columnConfig.isEditable !== true) {
        return false;
    }
    return areEventContainerGlobalConstraintsSatisfied(eventContainer, globalConstraints);
}

function getEditPanelFieldGridClassName(columnCount: number): string {
    if (columnCount === 3) {
        return "grid grid-cols-2 sm:grid-cols-3 gap-ds-space-150";
    }
    if (columnCount === 2) {
        return "grid grid-cols-2 gap-ds-space-150";
    }
    return "grid grid-flow-row gap-ds-space-150";
}

function parseUuidFromEventContainerCell(raw: unknown): Uuid | null {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw !== "string") {
        return null;
    }
    const s = raw.trim();
    if (s === "") {
        return null;
    }
    const parsed = uuidZodValidator.safeParse(s);
    if (parsed.success !== true) {
        return null;
    }
    return parsed.data;
}

function resolveAmaronAssignedAgentIdFromEventContainerCell(raw: unknown, users: Array<User>): Uuid | null {
    if (raw === null || raw === undefined) {
        return null;
    }
    const trimmed = String(raw).trim();
    if (trimmed === "") {
        return null;
    }
    const parsed = uuidZodValidator.safeParse(trimmed);
    if (parsed.success === true) {
        const userById = users.find((user) => user.userId === parsed.data);
        if (userById !== undefined) {
            return parsed.data;
        }
    }
    const user = findWorkspaceUserByStoredReferenceId(trimmed, users);
    if (user !== undefined) {
        return user.userId;
    }
    return null;
}

function resolveEmotoradAssignedDealerOuIdFromEventContainer(
    eventContainer: EventContainerWithEvents,
    eventContainerTemplate: EventContainerTemplate,
): Uuid | null {
    const fromAssignedDealerColumn = parseUuidFromEventContainerCell(
        eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID],
    );
    if (fromAssignedDealerColumn !== null) {
        return fromAssignedDealerColumn;
    }
    return getOptionalEventContainerOrganizationalUnitId(eventContainer, eventContainerTemplate);
}

function resolveEmotoradCustomerPincodeFromEventContainer(
    eventContainer: EventContainerWithEvents,
    additionalColumns: ReadonlyArray<UdoColumn>,
): string | null {
    const pincodeFromKnownColumn = eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID];
    if (pincodeFromKnownColumn !== null && pincodeFromKnownColumn !== undefined && String(pincodeFromKnownColumn).trim() !== "") {
        return String(pincodeFromKnownColumn).trim();
    }
    const pincodeDisplayNames = new Set(["customer pincode", "pincode", "customer_pincode", "customer pin code"]);
    for (const column of additionalColumns) {
        if (pincodeDisplayNames.has(column.displayName.trim().toLowerCase()) !== true) {
            continue;
        }
        const rawValue = eventContainer.additionalColumnValues[column.id];
        if (rawValue !== null && rawValue !== undefined && String(rawValue).trim() !== "") {
            return String(rawValue).trim();
        }
    }
    return null;
}

function buildEmotoradLifecycleOwnerSelectOptions(params: {
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    organizationalUnits: Array<OrganizationalUnit>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    assignedDealerOuId: Uuid | null;
    currentOwnerId: Uuid | null;
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
}): Array<{label: string; value: Uuid}> {
    const {users, userTemplates, organizationalUnits, organizationalUnitTemplates, assignedDealerOuId, currentOwnerId, platformUserDetailsByUserId} = params;

    if (assignedDealerOuId === null) {
        const fallback: Array<{label: string; value: Uuid}> = [];
        if (currentOwnerId !== null) {
            const ownerUser = users.find((u) => u.userId === currentOwnerId);
            if (ownerUser !== undefined) {
                fallback.push({
                    label: getUserDisplayName(currentOwnerId, users, userTemplates, platformUserDetailsByUserId),
                    value: currentOwnerId,
                });
            }
        }
        return fallback;
    }

    const eligibleUsers = users.filter((user) => {
        if (user.status !== UserStatus.active) {
            return false;
        }
        const hasOuAccess = userHasAccessToOrganisationalUnit({
            targetUser: user,
            users: users,
            userTemplates: userTemplates,
            organizationalUnits: organizationalUnits,
            organizationalUnitTemplates: organizationalUnitTemplates,
            targetOrganisationalUnitId: assignedDealerOuId,
        });
        return hasOuAccess === true && isLeafUser(user, userTemplates) === true;
    });

    const byName = [...eligibleUsers].sort((a, b) => {
        const nameA = getUserDisplayName(a.userId, users, userTemplates, platformUserDetailsByUserId).toLowerCase();
        const nameB = getUserDisplayName(b.userId, users, userTemplates, platformUserDetailsByUserId).toLowerCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    });

    const options: Array<{label: string; value: Uuid}> = byName.map((user) => ({
        label: getUserDisplayName(user.userId, users, userTemplates, platformUserDetailsByUserId),
        value: user.userId,
    }));

    if (currentOwnerId !== null && options.some((option) => option.value === currentOwnerId) !== true) {
        const ownerUser = users.find((u) => u.userId === currentOwnerId);
        if (ownerUser !== undefined) {
            options.unshift({
                label: getUserDisplayName(currentOwnerId, users, userTemplates, platformUserDetailsByUserId),
                value: currentOwnerId,
            });
        }
    }

    return options;
}

function readEventContainerSystemFieldFromRow(eventContainer: EventContainerWithEvents, columnId: string): unknown {
    return (eventContainer as Record<string, unknown>)[columnId];
}

function readEventSystemFieldFromRow(event: EventContainerWithEvents["events"][number], columnId: string): unknown {
    return (event as Record<string, unknown>)[columnId];
}

/**
 * Component to render eventContainer details columns based on the new eventContainerDetailsPage.columnGrouping configuration
 * Supports both editable (inline edit) and non-editable (view only) columns
 * For Event columns, returns eventTemplateId in the callback
 */
export function EventContainerDetailsColumns({
    columnGrouping,
    eventContainerTemplate,
    eventTemplates,
    eventContainerWithDiff,
    eventContainer,
    globalConstraints,
    onColumnValueChange,
}: EventContainerDetailsColumnsProps) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const users = useUsers();
    const userTemplate = useUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const products = useProductCatalogue() ?? [];
    const {workspaceCapabilities, platformUserDetails, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const platformUserDetailsByUserId = new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail]));
    const resolveUserDisplayName = (userId: Uuid | null) => getUserDisplayName(userId, users, userTemplate, platformUserDetailsByUserId);
    return (
        <ItemBuilder
            items={columnGrouping}
            itemBuilder={(columnGroup) => (
                <AnankeTempFormCollapsibleSection
                    title={columnGroup.displayName}
                    key={columnGroup.id}
                >
                    <ItemBuilder
                        items={columnGroup.columns}
                        itemBuilder={(columnConfig) => {
                            // Find the column definition based on columnType
                            let column: UdoColumn | undefined;
                            let eventTemplate: OttopilotEventTemplate | undefined;

                            if (columnConfig.columnType === "OttopilotEventContainer") {
                                // Find column from eventContainerTemplate
                                column = getAdditionalColumns(eventContainerTemplate).find((col) => col.id === columnConfig.columnId);
                            } else {
                                // Find column from the appropriate event template
                                if (columnConfig.eventTemplateId !== undefined) {
                                    eventTemplate = eventTemplates.find((et) => et.id === columnConfig.eventTemplateId);
                                    if (eventTemplate !== undefined) {
                                        column = getAdditionalColumns(eventTemplate).find((col) => col.id === columnConfig.columnId);
                                    }
                                }
                            }

                            // For additional columns that should exist but don't, log error
                            if (columnConfig.isAdditionalColumn && column === undefined) {
                                console.error(
                                    `Column not found: ${columnConfig.columnId} (columnType: ${columnConfig.columnType}, eventTemplateId: ${columnConfig.eventTemplateId})`,
                                );
                                return null;
                            }

                            // Get the value based on column type
                            const getValue = () => {
                                if (columnConfig.columnType === "OttopilotEventContainer") {
                                    if (columnConfig.isAdditionalColumn) {
                                        return eventContainerWithDiff.additionalColumnValues[columnConfig.columnId as Uuid];
                                    } else {
                                        return readEventContainerSystemFieldFromRow(eventContainerWithDiff, columnConfig.columnId as string);
                                    }
                                } else {
                                    // Event columns - get from the events array
                                    if (columnConfig.eventTemplateId !== undefined) {
                                        const event = eventContainerWithDiff.events.find((e) => e.templateId === columnConfig.eventTemplateId);
                                        if (event !== undefined) {
                                            if (columnConfig.isAdditionalColumn) {
                                                return event.additionalColumnValues[columnConfig.columnId as Uuid];
                                            } else {
                                                return readEventSystemFieldFromRow(event, columnConfig.columnId as string);
                                            }
                                        }
                                    }
                                    return null;
                                }
                            };

                            // Get the original value for comparison
                            const getOriginalValue = () => {
                                if (columnConfig.columnType === "OttopilotEventContainer") {
                                    if (columnConfig.isAdditionalColumn) {
                                        return eventContainer.additionalColumnValues[columnConfig.columnId as Uuid];
                                    } else {
                                        return readEventContainerSystemFieldFromRow(eventContainer, columnConfig.columnId as string);
                                    }
                                } else {
                                    if (columnConfig.eventTemplateId !== undefined) {
                                        const event = eventContainer.events.find((e) => e.templateId === columnConfig.eventTemplateId);
                                        if (event !== undefined) {
                                            if (columnConfig.isAdditionalColumn) {
                                                return event.additionalColumnValues[columnConfig.columnId as Uuid];
                                            } else {
                                                return readEventSystemFieldFromRow(event, columnConfig.columnId as string);
                                            }
                                        }
                                    }
                                    return null;
                                }
                            };

                            const value = getValue();
                            const originalValue = getOriginalValue();

                            const isAmaronAssignedAgentColumn =
                                isAmaronWorkspace(workspaceCapabilities) === true &&
                                columnConfig.columnType === "OttopilotEventContainer" &&
                                columnConfig.isAdditionalColumn === true &&
                                String(columnConfig.columnId) === String(AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID);

                            // Get string representation for view-only columns
                            const getStringValue = (): string => {
                                if (value === null || value === undefined) {
                                    return "-";
                                }

                                // For additional columns (isAdditionalColumn === true)
                                if (columnConfig.isAdditionalColumn && column !== undefined) {
                                    if (columnConfig.columnType === "OttopilotEventContainer") {
                                        // Use eventContainer stringValueGetter
                                        return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                            value: value as any,
                                            users: users,
                                            organizationalUnits: organizationalUnits,
                                            organizationalUnitTemplates: organizationalUnitTemplates,
                                            userTemplates: userTemplate,
                                            platformUserDetailsByUserId,
                                            products: products,
                                            userDefinedObjectOptionsByTemplateId,
                                        });
                                    } else {
                                        // Use event stringValueGetter
                                        return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                            value: value as any,
                                            users: users,
                                            userTemplates: userTemplate,
                                            platformUserDetailsByUserId,
                                            organizationalUnits: organizationalUnits,
                                            organizationalUnitTemplates: organizationalUnitTemplates,
                                            products: products,
                                            userDefinedObjectOptionsByTemplateId,
                                        });
                                    }
                                }

                                // For system columns (isAdditionalColumn === false)
                                // Resolve system-column formatting by column name.
                                const columnId = columnConfig.columnId as string;
                                const systemColumnDetails = getSystemAdditionalColumnDetails(columnId);
                                if (columnId === "createdBy" || columnId === "updatedBy") {
                                    return resolveUserDisplayName((value as Uuid | null) ?? null);
                                }
                                if (systemColumnDetails !== undefined) {
                                    return systemColumnDetails.stringValueGetter({
                                        value,
                                        users: users,
                                        userTemplates: userTemplate,
                                        platformUserDetailsByUserId,
                                    });
                                }

                                // Fallback: just return string representation
                                return String(value);
                            };

                            // Get options for select columns
                            const options = column === undefined
                                ? undefined
                                : getAdditionalColumnOptions(column, userDefinedObjectOptionsByTemplateId);

                            const renderAmaronAssignedAgentInlineEdit = () => {
                                const currentAssigneeId = resolveAmaronAssignedAgentIdFromEventContainerCell(value, users);
                                const selectOptions = buildAmaronAssignedAgentSelectOptions({
                                    users: users,
                                    userTemplates: userTemplate,
                                    organizationalUnits: organizationalUnits,
                                    organizationalUnitTemplates: organizationalUnitTemplates,
                                    pincodeOrganizationalUnitId: null,
                                    currentAssigneeId: currentAssigneeId,
                                    platformUserDetailsByUserId: platformUserDetailsByUserId,
                                });
                                const selectedLabel =
                                    currentAssigneeId === null
                                        ? ""
                                        : (selectOptions.find((item) => item.value === currentAssigneeId)?.label ?? resolveUserDisplayName(currentAssigneeId));

                                return (
                                    <AnankeTempInlineEditableSelect
                                        defaultValue={{
                                            value: currentAssigneeId ?? "",
                                            label: selectedLabel,
                                        }}
                                        onConfirm={(newValue: {value: string; label: string} | null) => {
                                            const nextAssigneeId = newValue === null || newValue.value.trim() === "" ? null : (newValue.value as Uuid);
                                            const previousAssigneeId = resolveAmaronAssignedAgentIdFromEventContainerCell(originalValue, users);
                                            if (nextAssigneeId === previousAssigneeId) {
                                                return;
                                            }
                                            onColumnValueChange(columnConfig.columnId as string, nextAssigneeId, undefined);
                                        }}
                                        options={selectOptions.map((item) => ({
                                            label: item.label,
                                            value: item.value,
                                        }))}
                                        placeholder="-"
                                        readViewFitContainerWidth={true}
                                    />
                                );
                            };

                            // Render inline edit component based on column type
                            const renderInlineEditComponent = () => {
                                if (column === undefined) {
                                    return null;
                                }

                                const productCatalogueTemplateId = getProductCatalogueTemplateIdFromAdditionalColumnConfiguration("configuration" in column ? column.configuration : undefined);

                                const handleSetValue = (nextState: any) => {
                                    if (nextState === originalValue) {
                                        return;
                                    }
                                    // Pass eventTemplateId if this is an Event column, undefined otherwise
                                    onColumnValueChange(
                                        columnConfig.columnId as string,
                                        nextState,
                                        columnConfig.columnType === "OttopilotEvent" ? columnConfig.eventTemplateId : undefined,
                                    );
                                };

                                const defaultValue = {
                                    label: (() => {
                                        if (options !== undefined) {
                                            const matchedOption = options.find((item) => item.id === value);
                                            return matchedOption?.displayName ?? "-";
                                        }
                                        return "Select an option";
                                    })(),
                                    value: value,
                                };

                                if (columnConfig.columnType === "OttopilotEventContainer") {
                                    // Use eventContainer inlineEditComponent
                                    return getUserDefinedObjectAdditionalColumnDetails(column).inlineEditComponent({
                                        value: value as any,
                                        setValue: handleSetValue,
                                        options: options as any,
                                        defaultValue: defaultValue,
                                        productCatalogueTemplateId: productCatalogueTemplateId,
                                    });
                                } else {
                                    // Use event inlineEditComponent
                                    return getUserDefinedObjectAdditionalColumnDetails(column).inlineEditComponent({
                                        value: value as any,
                                        setValue: handleSetValue,
                                        options: options as any,
                                        defaultValue: defaultValue,
                                        productCatalogueTemplateId: productCatalogueTemplateId,
                                    });
                                }
                            };

                            return (
                                <div
                                    className="grid grid-cols-[1fr_1fr] justify-start gap-4 px-ds-space-150 py-ds-space-100"
                                    key={columnConfig.id}
                                >
                                    <div
                                        className="font-ds-font-weight-medium leading-ds-space-250 text-[0.875rem] p-ds-space-100 break-words"
                                        style={{
                                            color: token("color.text"),
                                            font: "Inter",
                                        }}
                                        id={columnConfig.id}
                                    >
                                        <div className="w-full break-words">{columnConfig.label}</div>
                                    </div>
                                    <div className="break-words min-w-0">
                                        {column?.source.type === UdoColumnSource.resolver || getEventContainerDetailsColumnIsEditable(columnConfig, eventContainer, globalConstraints) !== true ||
                                        column === undefined ? (
                                            // Non-editable: show as view-only text
                                            <div
                                                className="p-ds-space-100 text-[0.875rem] leading-ds-space-250 break-words"
                                                style={{
                                                    color: token("color.text"),
                                                }}
                                            >
                                                {getStringValue()}
                                            </div>
                                        ) : isAmaronAssignedAgentColumn === true ? (
                                            renderAmaronAssignedAgentInlineEdit()
                                        ) : (
                                            // Editable: use inlineEditComponent
                                            renderInlineEditComponent()
                                        )}
                                    </div>
                                </div>
                            );
                        }}
                        spaceBuilder={(spaceIndex) => (
                            <AnankeTempVerticalSpacer
                                above="formRow"
                                below="formRow"
                                key={spaceIndex}
                            />
                        )}
                    />
                </AnankeTempFormCollapsibleSection>
            )}
            spaceBuilder={(spaceIndex) => (
                <AnankeTempVerticalSpacer
                    above="collapsibleSectionEnd"
                    below="collapsibleSectionStart"
                    key={spaceIndex}
                />
            )}
        />
    );
}

export function EventContainerEditPanelColumns({
    columnGrouping,
    eventContainerTemplate,
    eventTemplates,
    eventContainerWithDiff,
    eventContainer,
    onColumnValueChange,
    useInQueueEditLayout = false,
    fieldGridColumnCount,
    useDayPlanEditLayout = false,
    useAmaronLeadsStatusHeader = false,
    useRequiredAsteriskOnly = false,
    emotoradLifecycleOwnerColumnId,
    globalConstraints,
}: EventContainerEditPanelColumnsProps) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const users = useUsers();
    const userTemplate = useUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const products = useProductCatalogue() ?? [];
    const productCatalogueTemplates = useProductCatalogueTemplates();
    const {workspaceCapabilities, platformUserDetails, userDefinedObjectOptionsByTemplateId} = useWorkspace();
    const platformUserDetailsByUserId = new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail]));
    const resolveUserDisplayName = (userId: Uuid | null) => getUserDisplayName(userId, users, userTemplate, platformUserDetailsByUserId);
    const useMultiColumnFieldGrid = useInQueueEditLayout === true || useDayPlanEditLayout === true || fieldGridColumnCount !== undefined;
    const fieldGridGroupClassName = getEditPanelFieldGridClassName(fieldGridColumnCount ?? (useMultiColumnFieldGrid === true ? 2 : 1));
    return (
        <ItemBuilder
            items={columnGrouping}
            itemBuilder={(columnGroup) => {
                const renderColumnItem = (columnConfig: ColumnGroupWithOptionalIcon["columns"][number]) => {
                    // Find the column definition based on columnType
                    let column: UdoColumn | undefined;
                    let eventTemplate: OttopilotEventTemplate | undefined;

                    if (columnConfig.columnType === "OttopilotEventContainer") {
                        // Find column from eventContainerTemplate
                        column = getAdditionalColumns(eventContainerTemplate).find((col) => col.id === columnConfig.columnId);
                    } else {
                        // Find column from the appropriate event template
                        if (columnConfig.eventTemplateId !== undefined) {
                            eventTemplate = eventTemplates.find((et) => et.id === columnConfig.eventTemplateId);
                            if (eventTemplate !== undefined) {
                                column = getAdditionalColumns(eventTemplate).find((col) => col.id === columnConfig.columnId);
                            }
                        }
                    }

                    // For additional columns that should exist but don't, log error
                    if (columnConfig.isAdditionalColumn && column === undefined) {
                        console.error(
                            `Column not found: ${columnConfig.columnId} (columnType: ${columnConfig.columnType}, eventTemplateId: ${columnConfig.eventTemplateId})`,
                        );
                        return null;
                    }

                    // Get the value based on column type
                    const getValue = () => {
                        if (columnConfig.columnType === "OttopilotEventContainer") {
                            if (columnConfig.isAdditionalColumn) {
                                return eventContainerWithDiff.additionalColumnValues[columnConfig.columnId as Uuid];
                            } else {
                                return readEventContainerSystemFieldFromRow(eventContainerWithDiff, columnConfig.columnId as string);
                            }
                        } else {
                            // Event columns - get from the events array
                            if (columnConfig.eventTemplateId !== undefined) {
                                const event = eventContainerWithDiff.events.find((e) => e.templateId === columnConfig.eventTemplateId);
                                if (event !== undefined) {
                                    if (columnConfig.isAdditionalColumn) {
                                        return event.additionalColumnValues[columnConfig.columnId as Uuid];
                                    } else {
                                        return readEventSystemFieldFromRow(event, columnConfig.columnId as string);
                                    }
                                }
                            }
                            return null;
                        }
                    };

                    // Get the original value for comparison
                    const getOriginalValue = () => {
                        if (columnConfig.columnType === "OttopilotEventContainer") {
                            if (columnConfig.isAdditionalColumn) {
                                return eventContainer.additionalColumnValues[columnConfig.columnId as Uuid];
                            } else {
                                return readEventContainerSystemFieldFromRow(eventContainer, columnConfig.columnId as string);
                            }
                        } else {
                            if (columnConfig.eventTemplateId !== undefined) {
                                const event = eventContainer.events.find((e) => e.templateId === columnConfig.eventTemplateId);
                                if (event !== undefined) {
                                    if (columnConfig.isAdditionalColumn) {
                                        return event.additionalColumnValues[columnConfig.columnId as Uuid];
                                    } else {
                                        return readEventSystemFieldFromRow(event, columnConfig.columnId as string);
                                    }
                                }
                            }
                            return null;
                        }
                    };

                    const value = getValue();
                    const originalValue = getOriginalValue();

                    // Get string representation for view-only columns
                    const getStringValue = (): string => {
                        if (columnConfig.columnType === "OttopilotEventContainer") {
                            const geographyKind =
                                columnConfig.columnId === EMOTORAD__EVENT_CONTAINER__DEALER_CITY__COLUMN_ID
                                    ? ("city" as const)
                                    : columnConfig.columnId === EMOTORAD__EVENT_CONTAINER__DEALER_STATE__COLUMN_ID
                                      ? ("state" as const)
                                      : columnConfig.columnId === EMOTORAD__EVENT_CONTAINER__DEALER_ZONE__COLUMN_ID
                                        ? ("zone" as const)
                                        : null;
                            if (geographyKind !== null) {
                                return resolveEmotoradLifecycleDealerCityStateZone(
                                    eventContainerWithDiff,
                                    eventContainerTemplate,
                                    geographyKind,
                                    organizationalUnits,
                                    organizationalUnitTemplates,
                                );
                            }
                        }

                        if (value === null || value === undefined) {
                            return "-";
                        }

                        if (
                            isAmaronWorkspace(workspaceCapabilities) === true &&
                            String(columnConfig.columnId) === String(AMARON__EVENT_CONTAINER__SOURCE__PRIORITY__COLUMN_ID)
                        ) {
                            const score = typeof value === "number" ? value : Number(value);
                            if (Number.isFinite(score) === true) {
                                const formatted = formatAmaronSourcePriorityScoreForDisplay(score);
                                if (formatted !== "") {
                                    return formatted;
                                }
                            }
                            return "-";
                        }

                        // Emotorad: Assigned Dealer stores an OU UUID — resolve to OU display name
                        if (
                            columnConfig.isAdditionalColumn === true &&
                            String(columnConfig.columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID) &&
                            typeof value === "string"
                        ) {
                            const ou = organizationalUnits.find((unit) => unit.id === value);
                            if (ou !== undefined) {
                                const ouTemplate = organizationalUnitTemplates.find((t) => t.id === ou.templateId);
                                if (ouTemplate !== undefined) {
                                    return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                                }
                            }
                        }

                        // For additional columns (isAdditionalColumn === true)
                        if (columnConfig.isAdditionalColumn && column !== undefined) {
                            if (columnConfig.columnType === "OttopilotEventContainer") {
                                // Use eventContainer stringValueGetter
                                return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                    value: value as any,
                                    users: users,
                                    organizationalUnits: organizationalUnits,
                                    organizationalUnitTemplates: organizationalUnitTemplates,
                                    userTemplates: userTemplate,
                                    platformUserDetailsByUserId,
                                    products: products,
                                    userDefinedObjectOptionsByTemplateId,
                                });
                            } else {
                                // Use event stringValueGetter
                                return getUserDefinedObjectAdditionalColumnDetails(column).stringValueGetter({
                                    value: value as any,
                                    users: users,
                                    userTemplates: userTemplate,
                                    platformUserDetailsByUserId,
                                    organizationalUnits: organizationalUnits,
                                    organizationalUnitTemplates: organizationalUnitTemplates,
                                    products: products,
                                    userDefinedObjectOptionsByTemplateId,
                                });
                            }
                        }

                        // For system columns (isAdditionalColumn === false)
                        // Resolve system-column formatting by column name.
                        const columnId = columnConfig.columnId as string;
                        const systemColumnDetails = getSystemAdditionalColumnDetails(columnId);
                        if (columnId === "createdBy" || columnId === "updatedBy") {
                            return resolveUserDisplayName((value as Uuid | null) ?? null);
                        }
                        if (systemColumnDetails !== undefined) {
                            return systemColumnDetails.stringValueGetter({
                                value,
                                users: users,
                                userTemplates: userTemplate,
                                platformUserDetailsByUserId,
                            });
                        }

                        // Fallback: just return string representation
                        return String(value);
                    };

                    const options = column === undefined
                        ? undefined
                        : getAdditionalColumnOptions(column, userDefinedObjectOptionsByTemplateId);

                    const isEmotoradLifecycleOwnerUserColumn =
                        emotoradLifecycleOwnerColumnId !== undefined &&
                        columnConfig.columnType === "OttopilotEventContainer" &&
                        columnConfig.isAdditionalColumn === true &&
                        column !== undefined &&
                        column.type === UdoColumnType.user &&
                        String(columnConfig.columnId) === String(emotoradLifecycleOwnerColumnId);

                    const isEmotoradAssignedDealerColumn =
                        columnConfig.columnType === "OttopilotEventContainer" &&
                        columnConfig.isAdditionalColumn === true &&
                        String(columnConfig.columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID);

                    const isAmaronAssignedAgentColumn =
                        isAmaronWorkspace(workspaceCapabilities) === true &&
                        columnConfig.columnType === "OttopilotEventContainer" &&
                        columnConfig.isAdditionalColumn === true &&
                        String(columnConfig.columnId) === String(AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID);

                    const renderAmaronAssignedAgentSelectControl = () => {
                        const currentAssigneeId = resolveAmaronAssignedAgentIdFromEventContainerCell(value, users);
                        const selectOptions = buildAmaronAssignedAgentSelectOptions({
                            users,
                            userTemplates: userTemplate,
                            organizationalUnits,
                            organizationalUnitTemplates,
                            pincodeOrganizationalUnitId: null,
                            currentAssigneeId,
                            platformUserDetailsByUserId,
                        });
                        const selectedOption =
                            currentAssigneeId === null
                                ? null
                                : (selectOptions.find((item) => item.value === currentAssigneeId) ?? {
                                      label: resolveUserDisplayName(currentAssigneeId),
                                      value: currentAssigneeId,
                                  });

                        return (
                            <AnankeSelect
                                placeholder="Select agent"
                                options={selectOptions}
                                value={selectedOption}
                                onChange={(selected: unknown) => {
                                    const nextAssigneeId = selected === null || selected === undefined ? null : (selected as {value: Uuid}).value;
                                    const previousAssigneeId = resolveAmaronAssignedAgentIdFromEventContainerCell(originalValue, users);
                                    if (nextAssigneeId === previousAssigneeId) {
                                        return;
                                    }
                                    onColumnValueChange(
                                        columnConfig.columnId as string,
                                        nextAssigneeId,
                                        columnConfig.columnType === "OttopilotEvent" ? columnConfig.eventTemplateId : undefined,
                                    );
                                }}
                            />
                        );
                    };

                    const renderEmotoradLifecycleOwnerSelectControl = () => {
                        const assignedDealerOuId = resolveEmotoradAssignedDealerOuIdFromEventContainer(eventContainerWithDiff, eventContainerTemplate);
                        const currentOwnerId = parseUuidFromEventContainerCell(value);
                        const selectOptions = buildEmotoradLifecycleOwnerSelectOptions({
                            users: users,
                            userTemplates: userTemplate,
                            organizationalUnits: organizationalUnits,
                            organizationalUnitTemplates: organizationalUnitTemplates,
                            assignedDealerOuId: assignedDealerOuId,
                            currentOwnerId: currentOwnerId,
                            platformUserDetailsByUserId,
                        });
                        const selectedOption =
                            currentOwnerId === null
                                ? null
                                : (selectOptions.find((item) => item.value === currentOwnerId) ?? {
                                      label: resolveUserDisplayName(currentOwnerId),
                                      value: currentOwnerId,
                                  });

                        return (
                            <AnankeSelect
                                isDisabled={assignedDealerOuId === null}
                                placeholder={assignedDealerOuId === null ? "Select assigned dealer first" : "Select lifecycle owner"}
                                options={selectOptions}
                                value={selectedOption}
                                onChange={(selected: unknown) => {
                                    const nextValue = selected === null || selected === undefined ? "" : String((selected as {value: Uuid}).value);
                                    const prevString =
                                        originalValue === null || originalValue === undefined ? "" : typeof originalValue === "string" ? originalValue : "";
                                    if (nextValue === prevString) {
                                        return;
                                    }
                                    onColumnValueChange(columnConfig.columnId as string, nextValue, undefined);
                                }}
                            />
                        );
                    };

                    const renderEmotoradAssignedDealerSelectControl = () => {
                        const currentDealerId = resolveEmotoradAssignedDealerOuIdFromEventContainer(eventContainerWithDiff, eventContainerTemplate);
                        const customerPincode = resolveEmotoradCustomerPincodeFromEventContainer(
                            eventContainerWithDiff,
                            getAdditionalColumns(eventContainerTemplate),
                        );

                        return (
                            <EmotoradAssignedDealerSelect
                                customerPincode={customerPincode}
                                value={currentDealerId}
                                onChange={(dealerId) => {
                                    const nextValue = dealerId === null ? "" : dealerId;
                                    const prevString =
                                        originalValue === null || originalValue === undefined ? "" : typeof originalValue === "string" ? originalValue : "";
                                    if (nextValue === prevString) {
                                        return;
                                    }
                                    onColumnValueChange(columnConfig.columnId as string, nextValue, undefined);
                                }}
                            />
                        );
                    };

                    // Render inline edit component based on column type
                    const renderInlineEditComponent = () => {
                        if (column === undefined) {
                            return null;
                        }

                        const productCatalogueTemplateId = getProductCatalogueTemplateIdFromAdditionalColumnConfiguration("configuration" in column ? column.configuration : undefined);

                        const handleSetValue = (nextState: any) => {
                            if (nextState === originalValue) {
                                return;
                            }
                            // Pass eventTemplateId if this is an Event column, undefined otherwise
                            onColumnValueChange(
                                columnConfig.columnId as string,
                                nextState,
                                columnConfig.columnType === "OttopilotEvent" ? columnConfig.eventTemplateId : undefined,
                            );
                        };

                        if (columnConfig.columnType === "OttopilotEventContainer") {
                            // Use eventContainer inlineEditComponent
                            return getUserDefinedObjectAdditionalColumnDetails(column).editComponent({
                                value: value as any,
                                setValue: handleSetValue,
                                options: options as any,
                                productCatalogueTemplateId: productCatalogueTemplateId,
                            });
                        } else {
                            // Use event inlineEditComponent
                            return getUserDefinedObjectAdditionalColumnDetails(column).editComponent({
                                value: value as any,
                                setValue: handleSetValue,
                                options: options as any,
                                productCatalogueTemplateId: productCatalogueTemplateId,
                            });
                        }
                    };

                    const modelVariantOptions = [
                        {label: 'Trex Air - 29"', value: 'Trex Air - 29"'},
                        {label: 'Trex Air - 27.5"', value: 'Trex Air - 27.5"'},
                        {label: 'Trex Pro - 29"', value: 'Trex Pro - 29"'},
                    ];
                    const isModelVariantField =
                        columnConfig.label.toLowerCase().includes("model variant") || columnConfig.label.toLowerCase().includes("model varient");
                    const currentModelValue = value === null || value === undefined ? "" : String(value);
                    const resolveModelVariantLabel = (rawValue: string): string => {
                        if (rawValue === "") return "";
                        const product = products.find((p) => p.id === rawValue);
                        if (product !== undefined) {
                            const COLOR_NAME_COL = "ec16634a-156b-48eb-9d32-1364f0b80f42" as Uuid;
                            const colorName = product.additionalColumnValues[COLOR_NAME_COL];
                            if (typeof colorName === "string" && colorName.trim() !== "") {
                                return colorName;
                            }
                            return getProductCatalogueDisplayName(product, productCatalogueTemplates);
                        }
                        return rawValue;
                    };
                    const effectiveModelOptions =
                        currentModelValue !== "" && modelVariantOptions.some((item) => item.value === currentModelValue) !== true
                            ? [{label: resolveModelVariantLabel(currentModelValue), value: currentModelValue}, ...modelVariantOptions]
                            : modelVariantOptions;

                    if (useMultiColumnFieldGrid === true) {
                        return (
                            <div
                                key={`${eventContainer.id}-${columnConfig.id}`}
                                className="grid grid-flow-row gap-y-ds-space-050 min-w-0"
                            >
                                <label
                                    className="font-ds-font-weight-medium leading-ds-space-250 text-[0.875rem] break-words"
                                    style={{color: token("color.text.subtle"), font: "Inter"}}
                                    id={columnConfig.id}
                                >
                                    {columnConfig.label}
                                    {(
                                        useAmaronLeadsStatusHeader === true || useRequiredAsteriskOnly === true || useDayPlanEditLayout === true
                                            ? columnConfig.isRequired === true
                                            : useInQueueEditLayout === true
                                    ) ? (
                                        <span
                                            style={{color: token("color.text.danger")}}
                                            aria-hidden={true}
                                        >
                                            {" "}
                                            *
                                        </span>
                                    ) : null}
                                </label>
                                <div className="min-h-[40px] max-h-[40px]">
                                    {getEventContainerDetailsColumnIsEditable(columnConfig, eventContainer, globalConstraints) === true && column?.source.type === UdoColumnSource.physical &&
                                    isEmotoradAssignedDealerColumn ? (
                                        renderEmotoradAssignedDealerSelectControl()
                                    ) : getEventContainerDetailsColumnIsEditable(columnConfig, eventContainer, globalConstraints) === true && column?.source.type === UdoColumnSource.physical &&
                                      isEmotoradLifecycleOwnerUserColumn ? (
                                        renderEmotoradLifecycleOwnerSelectControl()
                                    ) : getEventContainerDetailsColumnIsEditable(columnConfig, eventContainer, globalConstraints) === true && column?.source.type === UdoColumnSource.physical &&
                                      isAmaronAssignedAgentColumn ? (
                                        renderAmaronAssignedAgentSelectControl()
                                    ) : getEventContainerDetailsColumnIsEditable(columnConfig, eventContainer, globalConstraints) === true && column?.source.type === UdoColumnSource.physical &&
                                      isModelVariantField ? (
                                        <AnankeSelect
                                            options={effectiveModelOptions}
                                            value={effectiveModelOptions.find((item) => item.value === currentModelValue) ?? null}
                                            onChange={(selectedOption: unknown) => {
                                                onColumnValueChange(
                                                    columnConfig.columnId as string,
                                                    (selectedOption as {value: string} | null)?.value ?? "",
                                                    columnConfig.columnType === "OttopilotEvent" ? columnConfig.eventTemplateId : undefined,
                                                );
                                            }}
                                            placeholder="Select model variant"
                                        />
                                    ) : column?.source.type === UdoColumnSource.resolver || getEventContainerDetailsColumnIsEditable(columnConfig, eventContainer, globalConstraints) !== true ||
                                      column === undefined ? (
                                        <AnankeTextField
                                            value={getStringValue()}
                                            isDisabled={true}
                                            readOnly={true}
                                        />
                                    ) : (
                                        renderInlineEditComponent()
                                    )}
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div
                            className="grid grid-cols-[1fr_1fr] justify-start gap-4 px-ds-space-150 py-ds-space-100"
                            key={`${eventContainer.id}-${columnConfig.id}`}
                        >
                            <div
                                className="font-ds-font-weight-medium leading-ds-space-250 text-[0.875rem] p-ds-space-100 break-words"
                                style={{
                                    color: token("color.text"),
                                    font: "Inter",
                                }}
                                id={columnConfig.id}
                            >
                                <div className="w-full break-words">{columnConfig.label}</div>
                            </div>
                            <div className="break-words min-w-0">
                                {column?.source.type === UdoColumnSource.resolver || getEventContainerDetailsColumnIsEditable(columnConfig, eventContainer, globalConstraints) !== true || column === undefined ? (
                                    // Non-editable: show as view-only text
                                    <div
                                        className="p-ds-space-100 text-[0.875rem] leading-ds-space-250 break-words"
                                        style={{
                                            color: token("color.text"),
                                        }}
                                    >
                                        {getStringValue()}
                                    </div>
                                ) : isEmotoradAssignedDealerColumn ? (
                                    renderEmotoradAssignedDealerSelectControl()
                                ) : isEmotoradLifecycleOwnerUserColumn ? (
                                    renderEmotoradLifecycleOwnerSelectControl()
                                ) : isAmaronAssignedAgentColumn ? (
                                    renderAmaronAssignedAgentSelectControl()
                                ) : (
                                    // Editable: use inlineEditComponent
                                    renderInlineEditComponent()
                                )}
                            </div>
                        </div>
                    );
                };

                const groupContent =
                    useMultiColumnFieldGrid === true ? (
                        <div className={fieldGridGroupClassName}>{columnGroup.columns.map((columnConfig) => renderColumnItem(columnConfig))}</div>
                    ) : (
                        <ItemBuilder
                            items={columnGroup.columns}
                            itemBuilder={(columnConfig) => renderColumnItem(columnConfig)}
                            spaceBuilder={(spaceIndex) => (
                                <AnankeTempVerticalSpacer
                                    above="formRow"
                                    below="formRow"
                                    key={spaceIndex}
                                />
                            )}
                        />
                    );

                if (columnGroup.icon !== undefined && columnGroup.iconColor !== undefined && columnGroup.iconBackgroundColor !== undefined) {
                    return (
                        <AnankeTempCollapsibleSectionWithIcon
                            key={columnGroup.id}
                            title={columnGroup.displayName}
                            icon={columnGroup.icon}
                            iconColor={columnGroup.iconColor}
                            iconBackgroundColor={columnGroup.iconBackgroundColor}
                            initialIsOpen={columnGroup.initialIsOpen}
                            className={columnGroup.className}
                        >
                            {groupContent}
                        </AnankeTempCollapsibleSectionWithIcon>
                    );
                }
                return (
                    <AnankeTempFormCollapsibleSection
                        key={columnGroup.id}
                        title={columnGroup.displayName}
                    >
                        {groupContent}
                    </AnankeTempFormCollapsibleSection>
                );
            }}
            spaceBuilder={(spaceIndex) => (
                <AnankeTempVerticalSpacer
                    above="collapsibleSectionEnd"
                    below="collapsibleSectionStart"
                    key={spaceIndex}
                />
            )}
        />
    );
}
